import { FC, useEffect, useState } from 'react';
import { endOfDay, isBefore, isAfter, parseISO, startOfDay } from 'date-fns';

import { Box, Loader, styled, Table, themeGet } from '@qwealth/qcore';
import { QTask } from '@qwealth/qdata';

import { selectTradeRequests, selectTradeRequestsIsLoading } from 'data/selectors/tradeRequests';
import { useAppSelector, useAppDispatch } from 'data/store';
import { TradeRequestFilters, TradeRequestItem } from 'data/models/TradeRequest';
import { useDebounce } from 'hooks/useDebounce';
import { createColumns, filteredTradeRequests, getSavedFilters, saveFiltersInLocalStorage } from 'data/helpers/tradeRequests';
import TradeRequestsHeader from './Header/TradeRequestsHeader';
import { getTradeRequests } from 'data/actions/tradeRequests';
import { EditActivityModal } from 'components/Households/Common/HouseholdActivities/EditActivityModal';
import { ACTIVITY_TYPE_TASKS, MODAL_MODE_VIEW } from 'data/models/Activity';

export const StyledTable = styled(Box)`
  thead {
    width: 100% !important;
    background-color: ${themeGet('colors.gray.3')};
    th {
      color: ${themeGet('colors.dark')} !important;
      text-transform: capitalize !important;
      font-size: ${themeGet('fontSizes.default')} !important;
    }
  }
  tbody {
    tr:hover {
      background: ${themeGet('colors.gray.3')} !important;
    }
  }
`;

const toQTask = (item: TradeRequestItem): Partial<QTask> => ({
  id: item.taskId,
  title: item.title,
  householdQID: item.householdQID,
  householdName: item.householdName,
  dueDate: item.dueDate,
  status: item.status ?? undefined,
  partnerID: item.partnerId,
  assignee: item.assigneeEmail ?? '',
  taskType: 'Trade Request',
  taskTags: item.tags.map(key => ({ partnerId: item.partnerId, key })),
  details: item.details ?? '',
  isInternal: 0,
  priority: 'Medium',
});

const TradeRequestsPage: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const tradeRequests = useAppSelector(selectTradeRequests);
  const isLoading = useAppSelector(selectTradeRequestsIsLoading);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<TradeRequestItem[]>(tradeRequests);
  const [mutableFilters, setMutableFilters] = useState<TradeRequestFilters>(getSavedFilters());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Partial<QTask> | null>(null);

  const query = useDebounce(searchQuery.toLowerCase(), 500);
  const visibleItems = filteredTradeRequests(filteredItems, query);

  const rowEvents = {
    onClick: (_e: React.MouseEvent, row: TradeRequestItem): void => {
      setSelectedTask(toQTask(row));
      setShowModal(true);
    },
  };

  const noDataIndication = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      );
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" fontSize="default">
        No records found.
      </Box>
    );
  };

  useEffect(() => {
    dispatch(getTradeRequests());
  }, [dispatch]);

  useEffect(() => {
    const filtered = tradeRequests.filter(item => {
      if (mutableFilters.status && item.status !== mutableFilters.status) {
        return false;
      }

      if (mutableFilters.partnerName && item.partnerName !== mutableFilters.partnerName) {
        return false;
      }

      if (mutableFilters.assignedTo && item.assignedTo !== mutableFilters.assignedTo) {
        return false;
      }

      if (mutableFilters.tag && !item.tags.includes(mutableFilters.tag)) {
        return false;
      }

      if (mutableFilters.dueDateStart && mutableFilters.dueDateEnd) {
        if (item.dueDate) {
          const itemDate = parseISO(item.dueDate);
          const start = parseISO(mutableFilters.dueDateStart);
          const end = parseISO(mutableFilters.dueDateEnd);
          if (!isAfter(itemDate, startOfDay(start)) || !isBefore(itemDate, endOfDay(end))) {
            return false;
          }
        } else {
          return false;
        }
      }

      return true;
    });

    setFilteredItems(filtered);
    saveFiltersInLocalStorage(mutableFilters);
  }, [mutableFilters, tradeRequests]);

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" backgroundColor="white" padding="large" boxShadow="layout" overflowY="hidden">
          <TradeRequestsHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            mutableFilters={mutableFilters}
            setMutableFilters={setMutableFilters}
          />
          <StyledTable>
            <Table
              // @ts-ignore
              keyField="taskId"
              data={visibleItems}
              columns={createColumns()}
              fixedHeader
              striped
              bordered={false}
              linkableRow
              rowEvents={rowEvents}
              tableHeight="700px"
              noDataIndication={noDataIndication}
            />
            <Box fontWeight="bold" mt="large" fontStyle="italic">
              Showing {visibleItems.length} results
            </Box>
          </StyledTable>
        </Box>
      )}

      <EditActivityModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          dispatch(getTradeRequests());
        }}
        type={ACTIVITY_TYPE_TASKS}
        initialTaskValues={selectedTask}
        initialLogValues={null}
        modalModeOption={MODAL_MODE_VIEW}
      />
    </>
  );
};

export default TradeRequestsPage;
